'use client';

import { useEffect, useRef } from 'react';

import { useProductDetails } from '@workspace/lib/hooks/offers/use-product-details';
import {
  SESSION_STORE_KEYS,
  useSessionStore,
} from '@workspace/lib/hooks/store/use-session-store';
import { STORE_KEYS, useStore } from '@workspace/lib/hooks/store/use-store';
import type { ProductDetails } from '@workspace/lib/types/business-logic/types';
import {
  ANALYTICS_TOUCH_INTERVAL_MS,
  touchAnalyticsStore,
} from '@workspace/lib/utils/analytics/analytics-local-storage';
import { saveEcommerceItem } from '@workspace/lib/utils/analytics/save-ecommerce-item';
import { getIpAddress } from '@workspace/lib/utils/get-ip-address';

export function ClientBootstrap() {
  const { getFromSessionStore, saveToSessionStore } = useSessionStore();
  const { getFromStore, saveToStore } = useStore();
  const listenerAttachedRef = useRef(false);
  const { getProductDetails } = useProductDetails();

  useEffect(() => {
    if (!getFromSessionStore(SESSION_STORE_KEYS.messageId)) {
      saveToSessionStore(SESSION_STORE_KEYS.messageId, crypto.randomUUID());
    }

    if (!getFromSessionStore(SESSION_STORE_KEYS.userId)) {
      saveToSessionStore(SESSION_STORE_KEYS.userId, crypto.randomUUID());
    }

    if (!getFromSessionStore(SESSION_STORE_KEYS.tmxSessionId)) {
      saveToSessionStore(SESSION_STORE_KEYS.tmxSessionId, crypto.randomUUID());
    }

    if (!getFromStore(STORE_KEYS.ip)) {
      (async () => {
        const ip = await getIpAddress();
        if (ip && typeof sessionStorage !== 'undefined') {
          saveToStore(STORE_KEYS.ip, ip);
        }
      })();
    }

    // check local storage pfid exist
    const params = new URLSearchParams(window.location.search);
    const storedRequestedProduct = getFromSessionStore(
      SESSION_STORE_KEYS.requestedProduct,
    ) as ProductDetails;
    const urlPfid = params.get('pfid')?.toLowerCase() ?? '';
    const fallbackPfid = (
      process.env.NEXT_PUBLIC_FALLBACK_PRODUCT_ISSUE_FEATURE_ID ?? ''
    ).toLowerCase();

    // Only overwrite the stored product when:
    // 1. There's an explicit pfid in the URL that differs from the current store, OR
    // 2. There's no product in the store at all (use fallback)
    //
    // CST-25139: Skip the fallback fetch on the broken-journey page. The BJ hook
    // fetches the correct product from the BJ status response and sets it via
    // setRequestedProductDetails. Fetching the fallback here would race against
    // that and potentially overwrite the correct BJ product with the default STL
    // product (NEXT_PUBLIC_FALLBACK_PRODUCT_ISSUE_FEATURE_ID).
    const isBrokenJourneyPage = window.location.pathname.includes('broken-journey');
    const queryStringPFid = urlPfid || (!isBrokenJourneyPage ? fallbackPfid : '');

    if (
      urlPfid &&
      storedRequestedProduct?.productIssueFeatureId?.toLowerCase() !== urlPfid
    ) {
      // URL has an explicit pfid that differs from what's stored - update
      (async () => {
        const productDetails = await getProductDetails(urlPfid);
        if (productDetails) {
          saveToSessionStore(
            SESSION_STORE_KEYS.requestedProduct,
            productDetails.requestedProduct,
          );

          if (productDetails.ecommerceItem) {
            saveEcommerceItem(productDetails.ecommerceItem);
          }
        }
      })();
    } else if (
      !isBrokenJourneyPage &&
      storedRequestedProduct?.productIssueFeatureId &&
      storedRequestedProduct.userFriendlyDescription === undefined
    ) {
      // Product in store but userFriendlyDescription is missing — its source (the
      // backend product or a calculator entry) didn't carry one. Fill in only the
      // display fields: the rest is submitted as productDetails and must keep the
      // decision values. A catalogue null is stored as '', hence === undefined.
      const pfidToFetch =
        storedRequestedProduct.productIssueFeatureId.toLowerCase();
      (async () => {
        const productDetails = await getProductDetails(pfidToFetch);
        if (!productDetails) return;

        // The BJ hook or reserve submit may have replaced the product meanwhile
        const currentProduct = getFromSessionStore(
          SESSION_STORE_KEYS.requestedProduct,
        ) as ProductDetails | undefined;
        if (
          !currentProduct ||
          currentProduct.productIssueFeatureId?.toLowerCase() !== pfidToFetch
        ) {
          return;
        }

        saveToSessionStore(SESSION_STORE_KEYS.requestedProduct, {
          ...currentProduct,
          userFriendlyDescription:
            currentProduct.userFriendlyDescription ??
            productDetails.requestedProduct.userFriendlyDescription,
          productCategory:
            currentProduct.productCategory ??
            productDetails.requestedProduct.productCategory,
        });
        if (productDetails.ecommerceItem) {
          saveEcommerceItem({
            ...productDetails.ecommerceItem,
            price:
              currentProduct.requestedAmount ??
              productDetails.ecommerceItem.price,
          });
        }
      })();
    } else if (!storedRequestedProduct && queryStringPFid) {
      // No product in store at all - use fallback
      (async () => {
        const productDetails = await getProductDetails(queryStringPFid);
        if (productDetails) {
          saveToSessionStore(
            SESSION_STORE_KEYS.requestedProduct,
            productDetails.requestedProduct,
          );

          if (productDetails.ecommerceItem) {
            saveEcommerceItem(productDetails.ecommerceItem);
          }
        }
      })();
    }

    if (!getFromStore(STORE_KEYS.lastVisitDate)) {
      saveToStore(STORE_KEYS.lastVisitDate, new Date().toISOString());
    }

    if (!getFromSessionStore(SESSION_STORE_KEYS.sessionStartTime)) {
      saveToSessionStore(
        SESSION_STORE_KEYS.sessionStartTime,
        new Date().toISOString(),
      );
    }

    // Initialize or increment number of visits in current session
    const currentVisits =
      (getFromSessionStore(SESSION_STORE_KEYS.numberOfVisits) as number) || 0;
    saveToSessionStore(SESSION_STORE_KEYS.numberOfVisits, currentVisits + 1);

    // Expose 'resetStore' and 'resetSessionStore' dev helpers to global window object
    if (
      typeof window !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      if (!(window as any).resetAppStore) {
        (window as any).resetAppStore = () => {
          useStore.getState().resetStore();
          console.log('Store has been reset!');
        };
      }

      if (!(window as any).resetAppSessionStore) {
        (window as any).resetAppSessionStore = () => {
          useSessionStore.getState().resetSessionStore();
          console.log('Session store has been reset!');
        };
      }
    }

    // Update lastVisitDate when page is unloaded
    const handleBeforeUnload = () => {
      saveToStore(STORE_KEYS.lastVisitDate, new Date().toISOString());
    };

    if (!listenerAttachedRef.current) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      listenerAttachedRef.current = true;
    }

    return () => {
      if (listenerAttachedRef.current) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        listenerAttachedRef.current = false;
      }
    };
  }, [getFromSessionStore, saveToSessionStore, getFromStore, saveToStore]);

  // Keep the 24-hour analytics localStorage TTL rolling during active use
  useEffect(() => {
    const intervalId = setInterval(
      touchAnalyticsStore,
      ANALYTICS_TOUCH_INTERVAL_MS,
    );
    return () => clearInterval(intervalId);
  }, []);

  return null;
}
